import { ACCOUNTING_TRIAL_BALANCE, CLIENT_DASHBOARD_READ, CLIENT_HOLDINGS_READ_OWN, DASHBOARD_VIEW, HOLDINGS_VIEW, IMPORT_LIST, PLATFORM_DASHBOARD_READ, RECONCILIATION_LIST, CLIENT_GOVERNANCE_RECORD, RECONCILIATION_RUN_BREAKS_LIST, REPORTS_VIEW, TRANSACTION_LIST, REGULATORY_REPORT_RUN_EXPORT, REGULATORY_REPORT_RUN_GENERATE, REGULATORY_REPORT_RUN_LIST, REGULATORY_REPORT_RUN_CANCEL, REGULATORY_REPORT_RUN_RETRY, REGULATORY_REPORT_RUN_VALIDATE, REGULATORY_REPORT_DEFINITION_CREATE, REGULATORY_REPORT_DEFINITION_UPDATE, REGULATORY_REPORT_DEFINITION_DISABLE, REPORT_EXPORT, } from '../operations/index.js';
import { FEATURE_KEYS, MODULE_KEYS, OPERATION_IDS } from './routes/ids.js';
const tk = (value) => value;
const qdi = (value) => value;
const fid = (value) => value;
const formId = (value) => value;
const command = (value) => value;
const telemetry = (value) => value;
const operation = (value) => value;
export const REPORT_TYPES = Object.freeze([
    'PORTFOLIO_HOLDINGS',
    'CLIENT_HOLDINGS',
    'ACCOUNTING_TRIAL_BALANCE',
    'ACCOUNTING_FINANCIAL_STATEMENTS',
    'ACCOUNTING_PROFIT_AND_LOSS',
    'RECONCILIATION_BREAKS',
    'TRANSACTION_ACTIVITY',
    'CLIENT_STATEMENT',
    'SEBON_MONTHLY',
    'IRD_CGT',
    'NRB_QUARTERLY',
    'FIU_AML_CTF',
]);
export const REPORT_IDS = Object.freeze({
    PORTFOLIO_HOLDINGS: 'report.portfolio.holdings',
    CLIENT_HOLDINGS: 'report.client.holdings',
    ACCOUNTING_TRIAL_BALANCE: 'report.accounting.trial-balance',
    ACCOUNTING_FINANCIAL_STATEMENTS: 'report.accounting.financial-statements',
    ACCOUNTING_PROFIT_AND_LOSS: 'report.accounting.profit-and-loss',
    RECONCILIATION_BREAKS: 'report.reconciliation.breaks',
    TRANSACTION_ACTIVITY: 'report.transaction.activity',
    CLIENT_STATEMENT: 'report.client.statement',
    SEBON_MONTHLY: 'report.regulatory.sebon-monthly',
    IRD_CGT: 'report.regulatory.ird-cgt',
    NRB_QUARTERLY: 'report.regulatory.nrb-quarterly',
    FIU_AML_CTF: 'report.regulatory.fiu-aml-ctf',
});
export const DASHBOARD_IDS = Object.freeze({
    PLATFORM: 'dashboard.platform.operations',
    ORGANIZATION: 'dashboard.organization.portfolio',
    CLIENT: 'dashboard.client.portfolio',
});
function metadata(id, module, feature, operationId, privacyClassification, accessibilityLabelKey) {
    return {
        id,
        schemaVersion: 1,
        module,
        feature,
        operationId: operation(operationId),
        privacyClassification,
        telemetryId: telemetry(`experience.${id}`),
        accessibilityLabelKey: tk(accessibilityLabelKey),
        availability: 'AVAILABLE',
        owner: 'reports-dashboard',
    };
}
function field(id, name, labelKey, fieldType, options, overrides = {}) {
    return {
        id: fid(id),
        name,
        labelKey: tk(labelKey),
        fieldType,
        options: options?.map((option) => ({ value: option.value, labelKey: tk(option.labelKey) })),
        value: { submitBehavior: 'include', hiddenValuePolicy: 'unregister' },
        layout: { xs: 12, sm: 6, md: 4 },
        ...overrides,
    };
}
const requiredField = () => ({
    rules: [{ ruleId: 'required', messageKey: tk('validation.required') }],
    validateWhen: 'submit',
});
function reportParameterForm(id, operationId, fields) {
    return {
        ...metadata(id, MODULE_KEYS.REPORTS, FEATURE_KEYS.REPORTS_VIEW, operationId, 'FINANCIAL', `a11y.${id}`),
        id: formId(id),
        titleKey: tk('reports.liveParameters.title'),
        descriptionKey: tk('reports.liveParameters.description'),
        mode: 'create',
        fields,
        actions: [
            {
                id: `action.${id}.run`,
                schemaVersion: 1,
                module: MODULE_KEYS.REPORTS,
                feature: FEATURE_KEYS.REPORTS_VIEW,
                privacyClassification: 'FINANCIAL',
                telemetryId: `experience.${id}.run`,
                accessibilityLabelKey: tk('reports.liveParameters.run'),
                availability: 'AVAILABLE',
                owner: 'experience-registry',
                labelKey: tk('reports.liveParameters.run'),
                iconKey: 'play',
                kind: 'command',
                commandId: `reports.live.run.${id}`,
                method: 'GET',
                intent: 'primary',
                idempotencyRequired: false,
                submitBehavior: 'form_submit',
                submitConfig: {
                    validatesForm: true,
                    requiresDirty: false,
                    requiresValid: true,
                    resetBehavior: 'none',
                },
                successBehavior: 'NO_OP',
                errorBehavior: 'SHOW_INLINE_ERROR',
                errorMessageKey: tk('reports.liveParameters.requestFailed'),
                order: 10,
                group: 'primary',
            },
        ],
        submission: {
            defaultCommandId: `reports.live.run.${id}`,
            includeFields: fields.map((item) => item.id),
        },
    };
}
export const REPORT_PARAMETER_FORM = {
    ...metadata('form.reports.parameters', MODULE_KEYS.REPORTS, FEATURE_KEYS.REPORTS_VIEW, REPORTS_VIEW, 'FINANCIAL', 'a11y.form.reports.parameters'),
    id: formId('form.reports.parameters'),
    titleKey: tk('reports.parameters.title'),
    descriptionKey: tk('reports.parameters.description'),
    mode: 'create',
    fields: [
        field('field.reports.reportType', 'reportType', 'reports.parameters.reportType', 'select', [
            { value: 'SEBON_MONTHLY', labelKey: 'reports.types.sebonMonthly' },
            { value: 'IRD_CGT', labelKey: 'reports.types.irdCgt' },
            { value: 'NRB_QUARTERLY', labelKey: 'reports.types.nrbQuarterly' },
            { value: 'FIU_AML_CTF', labelKey: 'reports.types.fiuAmlCtf' },
        ]),
        field('field.reports.periodStart', 'reportingPeriodStart', 'reports.parameters.periodStart', 'date'),
        field('field.reports.periodEnd', 'reportingPeriodEnd', 'reports.parameters.periodEnd', 'date'),
        field('field.reports.fiscalYear', 'fiscalYear', 'reports.parameters.fiscalYear', 'text'),
        field('field.reports.year', 'year', 'reports.parameters.year', 'number'),
        field('field.reports.month', 'month', 'reports.parameters.month', 'number'),
        field('field.reports.quarter', 'quarter', 'reports.parameters.quarter', 'number'),
        field('field.reports.clientAccountId', 'clientAccountId', 'reports.parameters.clientAccount', 'select', undefined, {
            optionSource: {
                adapterId: 'api',
                operationId: OPERATION_IDS.ORGANIZATION_CLIENTS_LIST,
                valuePath: 'id',
                labelPath: 'fullName',
                allowSearch: true,
                minimumSearchLength: 0,
            },
        }),
    ],
    actions: [],
    submission: {
        defaultCommandId: command('reports.parameters.run'),
        includeFields: [
            fid('field.reports.reportType'),
            fid('field.reports.periodStart'),
            fid('field.reports.periodEnd'),
            fid('field.reports.fiscalYear'),
            fid('field.reports.year'),
            fid('field.reports.month'),
            fid('field.reports.quarter'),
            fid('field.reports.clientAccountId'),
        ],
    },
};
const HOLDINGS_FIELDS = [
    field('field.reports.live.asOfDate', 'asOfDate', 'reports.liveParameters.asOfDate', 'date'),
    field('field.reports.live.clientAccountId', 'clientAccountId', 'reports.liveParameters.clientAccount', 'select', undefined, {
        optionSource: {
            adapterId: 'api',
            operationId: OPERATION_IDS.ORGANIZATION_CLIENTS_LIST,
            valuePath: 'id',
            labelPath: 'fullName',
            allowSearch: true,
            minimumSearchLength: 0,
        },
    }),
    field('field.reports.live.portfolioAccountId', 'portfolioAccountId', 'reports.liveParameters.portfolioAccount', 'select', undefined, {
        optionSource: {
            adapterId: 'api',
            operationId: OPERATION_IDS.PORTFOLIO_ACCOUNT_LIST,
            valuePath: 'id',
            labelPath: 'accountNumber',
            allowSearch: true,
            minimumSearchLength: 0,
            queryDependencies: ['field.reports.live.clientAccountId'],
            queryParams: {
                clientAccountId: { fieldId: 'field.reports.live.clientAccountId' },
            },
        },
    }),
];
export const HOLDINGS_REPORT_PARAMETER_FORM = reportParameterForm('form.reports.holdingsParameters', HOLDINGS_VIEW, HOLDINGS_FIELDS);
export const CLIENT_HOLDINGS_REPORT_PARAMETER_FORM = reportParameterForm('form.reports.clientHoldingsParameters', CLIENT_HOLDINGS_READ_OWN, HOLDINGS_FIELDS);
export const TRIAL_BALANCE_REPORT_PARAMETER_FORM = reportParameterForm('form.reports.trialBalanceParameters', ACCOUNTING_TRIAL_BALANCE, [
    field('field.reports.trialBalance.asOfDate', 'asOfDate', 'reports.liveParameters.asOfDate', 'date'),
]);
export const FINANCIAL_STATEMENTS_REPORT_PARAMETER_FORM = reportParameterForm('form.reports.financialStatementsParameters', ACCOUNTING_TRIAL_BALANCE, [
    field('field.reports.financialStatements.periodStart', 'periodStart', 'reports.liveParameters.periodStart', 'date'),
    field('field.reports.financialStatements.asOfDate', 'asOfDate', 'reports.liveParameters.asOfDate', 'date'),
]);
export const RECONCILIATION_REPORT_PARAMETER_FORM = reportParameterForm('form.reports.reconciliationParameters', RECONCILIATION_RUN_BREAKS_LIST, [
    field('field.reports.live.runId', 'runId', 'reports.liveParameters.reconciliationRun', 'text'),
    field('field.reports.live.status', 'status', 'reports.liveParameters.status', 'select', [
        { value: 'OPEN', labelKey: 'reports.filters.status.open' },
        { value: 'RESOLVED', labelKey: 'reports.filters.status.resolved' },
        { value: 'APPROVED', labelKey: 'reports.filters.status.approved' },
        { value: 'REJECTED', labelKey: 'reports.filters.status.rejected' },
    ]),
    field('field.reports.live.severity', 'severity', 'reports.liveParameters.severity', 'select', [
        { value: 'INFO', labelKey: 'reports.filters.severity.info' },
        { value: 'WARNING', labelKey: 'reports.filters.severity.warning' },
        { value: 'CRITICAL', labelKey: 'reports.filters.severity.critical' },
    ]),
]);
export const TRANSACTION_ACTIVITY_REPORT_PARAMETER_FORM = reportParameterForm('form.reports.transactionActivityParameters', REPORTS_VIEW, [
    field('field.reports.activity.from', 'from', 'reports.liveParameters.from', 'date'),
    field('field.reports.activity.to', 'to', 'reports.liveParameters.to', 'date'),
    field('field.reports.activity.status', 'status', 'reports.liveParameters.status', 'select', [
        { value: 'SUBMITTED', labelKey: 'reports.filters.status.submitted' },
        { value: 'PENDING_APPROVAL', labelKey: 'reports.filters.status.pendingApproval' },
        { value: 'POSTED', labelKey: 'reports.filters.status.posted' },
        { value: 'REJECTED', labelKey: 'reports.filters.status.rejected' },
        { value: 'CANCELLED', labelKey: 'reports.filters.status.cancelled' },
    ]),
    field('field.reports.activity.type', 'type', 'reports.liveParameters.transactionType', 'select', [
        { value: 'BUY', labelKey: 'reports.filters.type.buy' },
        { value: 'SELL', labelKey: 'reports.filters.type.sell' },
        { value: 'CORPORATE_ACTION', labelKey: 'reports.filters.type.corporateAction' },
        { value: 'TRANSFER', labelKey: 'reports.filters.type.transfer' },
    ]),
    field('field.reports.activity.instrument', 'instrumentSymbol', 'reports.liveParameters.instrument', 'text'),
    field('field.reports.activity.portfolio', 'portfolioAccountId', 'reports.liveParameters.portfolioAccount', 'select', undefined, {
        optionSource: {
            adapterId: 'api',
            operationId: OPERATION_IDS.ORGANIZATION_PORTFOLIOS_LIST,
            valuePath: 'id',
            labelPath: 'accountNumber',
            allowSearch: true,
            minimumSearchLength: 0,
        },
    }),
]);
function formAction(id, labelKey, operationId, commandId) {
    return {
        ...metadata(`action.${id}`, MODULE_KEYS.REPORTS, FEATURE_KEYS.REPORTS_VIEW, operationId, 'FINANCIAL', `a11y.${id}`),
        id: `action.${id}`,
        labelKey: tk(labelKey),
        kind: 'command',
        submitBehavior: 'form_submit',
        submitConfig: {
            validatesForm: true,
            requiresDirty: false,
            requiresValid: true,
            resetBehavior: 'none',
        },
        commandId: command(commandId),
        method: 'POST',
        intent: 'primary',
        idempotencyRequired: true,
        successBehavior: 'CLOSE_DIALOG',
        errorBehavior: 'SHOW_INLINE_ERROR',
        errorMessageKey: tk('common.error.unexpected'),
        availability: 'AVAILABLE',
        order: 20,
        group: 'primary',
    };
}
function cancelAction() {
    return {
        ...metadata('action.reports.cancel', MODULE_KEYS.REPORTS, FEATURE_KEYS.REPORTS_VIEW, REPORTS_VIEW, 'INTERNAL', 'a11y.action.reports.cancel'),
        id: 'action.reports.cancel',
        labelKey: tk('common.cancel'),
        kind: 'localUi',
        submitBehavior: 'standalone',
        intent: 'secondary',
        idempotencyRequired: false,
        successBehavior: 'CLOSE_DIALOG',
        errorBehavior: 'SHOW_INLINE_ERROR',
        availability: 'AVAILABLE',
        order: 10,
        group: 'secondary',
    };
}
export const REPORT_DEFINITION_FORM = {
    ...metadata('form.reports.definition', MODULE_KEYS.REPORTS, FEATURE_KEYS.REPORTS_VIEW, REGULATORY_REPORT_DEFINITION_CREATE, 'FINANCIAL', 'a11y.form.reports.definition'),
    id: formId('form.reports.definition'),
    titleKey: tk('reports.definitionForm.title'),
    descriptionKey: tk('reports.definitionForm.description'),
    mode: 'create',
    fields: [
        field('field.reports.definition.code', 'reportCode', 'reports.definitionForm.code', 'text', undefined, { validation: requiredField() }),
        field('field.reports.definition.name', 'reportName', 'reports.definitionForm.name', 'text', undefined, { validation: requiredField() }),
        field('field.reports.definition.type', 'reportType', 'reports.definitionForm.type', 'select', [
            { value: 'SEBON_MONTHLY', labelKey: 'reports.types.sebonMonthly' },
            { value: 'IRD_CGT', labelKey: 'reports.types.irdCgt' },
            { value: 'NRB_QUARTERLY', labelKey: 'reports.types.nrbQuarterly' },
            { value: 'FIU_AML_CTF', labelKey: 'reports.types.fiuAmlCtf' },
        ], { validation: requiredField() }),
        field('field.reports.definition.body', 'regulatoryBody', 'reports.definitionForm.body', 'text'),
    ],
    actions: [
        cancelAction(),
        formAction('reports.saveDefinition', 'reports.actions.saveDefinition', REGULATORY_REPORT_DEFINITION_CREATE, 'reports.definition.save'),
    ],
    submission: {
        defaultCommandId: command('reports.definition.save'),
        includeFields: [
            fid('field.reports.definition.code'),
            fid('field.reports.definition.name'),
            fid('field.reports.definition.type'),
            fid('field.reports.definition.body'),
        ],
    },
};
export const REPORT_RUN_FORM = {
    ...metadata('form.reports.run', MODULE_KEYS.REPORTS, FEATURE_KEYS.REPORTS_VIEW, REGULATORY_REPORT_RUN_GENERATE, 'FINANCIAL', 'a11y.form.reports.run'),
    id: formId('form.reports.run'),
    titleKey: tk('reports.runForm.title'),
    descriptionKey: tk('reports.runForm.description'),
    mode: 'create',
    fields: [
        field('field.reports.run.definition', 'reportDefinitionId', 'reports.runForm.definition', 'select', undefined, { validation: requiredField() }),
        field('field.reports.run.periodStart', 'reportingPeriodStart', 'reports.runForm.start', 'date', undefined, { validation: requiredField() }),
        field('field.reports.run.periodEnd', 'reportingPeriodEnd', 'reports.runForm.end', 'date', undefined, { validation: requiredField() }),
    ],
    actions: [
        cancelAction(),
        formAction('reports.generateRun', 'reports.actions.generateRun', REGULATORY_REPORT_RUN_GENERATE, 'reports.run.generate'),
    ],
    submission: {
        defaultCommandId: command('reports.run.generate'),
        includeFields: [
            fid('field.reports.run.definition'),
            fid('field.reports.run.periodStart'),
            fid('field.reports.run.periodEnd'),
        ],
    },
};
export const IRD_FISCAL_YEAR_RUN_FORM = {
    ...metadata('form.reports.run.ird-fiscal-year', MODULE_KEYS.REPORTS, FEATURE_KEYS.REPORTS_VIEW, REGULATORY_REPORT_RUN_GENERATE, 'FINANCIAL', 'a11y.form.reports.runIrdFiscalYear'),
    id: formId('form.reports.run.ird-fiscal-year'),
    titleKey: tk('reports.runForm.irdFiscalYear.title'),
    descriptionKey: tk('reports.runForm.irdFiscalYear.description'),
    mode: 'create',
    fields: [
        field('field.reports.run.ird-fiscal-year.definition', 'reportDefinitionId', 'reports.runForm.definition', 'select', undefined, { validation: requiredField() }),
        field('field.reports.run.ird-fiscal-year.fiscalYear', 'fiscalYear', 'reports.runForm.irdFiscalYear.fiscalYear', 'text', undefined, {
            validation: {
                ...requiredField(),
                rules: [
                    ...requiredField().rules,
                    { ruleId: 'pattern', messageKey: tk('validation.patternFiscalYear') },
                ],
            },
        }),
        field('field.reports.run.ird-fiscal-year.periodStart', 'reportingPeriodStart', 'reports.runForm.start', 'date', undefined, { validation: requiredField() }),
        field('field.reports.run.ird-fiscal-year.periodEnd', 'reportingPeriodEnd', 'reports.runForm.end', 'date', undefined, { validation: requiredField() }),
    ],
    actions: [
        cancelAction(),
        formAction('reports.generateRun', 'reports.actions.generateRun', REGULATORY_REPORT_RUN_GENERATE, 'reports.run.generate'),
    ],
    submission: {
        defaultCommandId: command('reports.run.generate'),
        includeFields: [
            fid('field.reports.run.ird-fiscal-year.definition'),
            fid('field.reports.run.ird-fiscal-year.fiscalYear'),
            fid('field.reports.run.ird-fiscal-year.periodStart'),
            fid('field.reports.run.ird-fiscal-year.periodEnd'),
        ],
    },
};
function column(id, fieldName, labelKey, fieldType, operationId) {
    return {
        ...metadata(id, MODULE_KEYS.REPORTS, FEATURE_KEYS.REPORTS_VIEW, operationId, 'FINANCIAL', labelKey),
        labelKey: tk(labelKey),
        field: fieldName,
        fieldType,
        defaultVisible: true,
        hideable: true,
    };
}
const REPORT_COLUMNS = [
    column('column.report.id', 'id', 'reports.columns.id', 'text', REPORTS_VIEW),
    column('column.report.status', 'status', 'reports.columns.status', 'status', REPORTS_VIEW),
    column('column.report.periodStart', 'reportingPeriodStart', 'reports.columns.periodStart', 'date', REPORTS_VIEW),
    column('column.report.periodEnd', 'reportingPeriodEnd', 'reports.columns.periodEnd', 'date', REPORTS_VIEW),
    column('column.report.createdAt', 'createdAt', 'reports.columns.createdAt', 'datetime', REPORTS_VIEW),
].map((item) => ({ ...item, defaultVisible: item.field !== 'id', hideable: item.field === 'id' }));
function holdingsReportColumns(operationId) {
    return [
        column(`column.report.holdings.${operationId}.client`, 'clientAccountId', 'reports.columns.clientAccount', 'text', operationId),
        column(`column.report.holdings.${operationId}.portfolio`, 'portfolioAccountId', 'reports.columns.portfolioAccount', 'text', operationId),
        column(`column.report.holdings.${operationId}.instrument`, 'instrumentSymbol', 'reports.columns.instrument', 'text', operationId),
        column(`column.report.holdings.${operationId}.currency`, 'currency', 'reports.columns.currency', 'text', operationId),
        column(`column.report.holdings.${operationId}.quantity`, 'quantity', 'reports.columns.quantity', 'number', operationId),
        column(`column.report.holdings.${operationId}.costBasis`, 'costBasis', 'reports.columns.costBasis', 'money', operationId),
        column(`column.report.holdings.${operationId}.marketPrice`, 'marketPrice', 'reports.columns.marketPrice', 'money', operationId),
        column(`column.report.holdings.${operationId}.marketValue`, 'marketValue', 'reports.columns.marketValue', 'money', operationId),
        column(`column.report.holdings.${operationId}.unrealized`, 'unrealizedGainLoss', 'reports.columns.unrealizedGainLoss', 'money', operationId),
        column(`column.report.holdings.${operationId}.sourceAsOf`, 'sourceAsOf', 'reports.columns.sourceAsOf', 'datetime', operationId),
    ];
}
const TRIAL_BALANCE_COLUMNS = [
    column('column.report.trialBalance.code', 'code', 'reports.columns.accountCode', 'text', ACCOUNTING_TRIAL_BALANCE),
    column('column.report.trialBalance.name', 'name', 'reports.columns.accountName', 'text', ACCOUNTING_TRIAL_BALANCE),
    column('column.report.trialBalance.type', 'accountType', 'reports.columns.accountType', 'text', ACCOUNTING_TRIAL_BALANCE),
    column('column.report.trialBalance.opening', 'openingBalance', 'reports.columns.openingBalance', 'money', ACCOUNTING_TRIAL_BALANCE),
    column('column.report.trialBalance.movementDebit', 'movementDebit', 'reports.columns.movementDebit', 'money', ACCOUNTING_TRIAL_BALANCE),
    column('column.report.trialBalance.movementCredit', 'movementCredit', 'reports.columns.movementCredit', 'money', ACCOUNTING_TRIAL_BALANCE),
    column('column.report.trialBalance.debit', 'debit', 'reports.columns.closingDebit', 'money', ACCOUNTING_TRIAL_BALANCE),
    column('column.report.trialBalance.credit', 'credit', 'reports.columns.closingCredit', 'money', ACCOUNTING_TRIAL_BALANCE),
    column('column.report.trialBalance.balance', 'balance', 'reports.columns.signedBalance', 'money', ACCOUNTING_TRIAL_BALANCE),
];
const FINANCIAL_STATEMENTS_COLUMNS = [
    column('column.report.financialStatements.statement', 'statement', 'reports.columns.statement', 'text', ACCOUNTING_TRIAL_BALANCE),
    column('column.report.financialStatements.code', 'code', 'reports.columns.accountCode', 'text', ACCOUNTING_TRIAL_BALANCE),
    column('column.report.financialStatements.name', 'name', 'reports.columns.accountName', 'text', ACCOUNTING_TRIAL_BALANCE),
    column('column.report.financialStatements.type', 'accountType', 'reports.columns.accountType', 'text', ACCOUNTING_TRIAL_BALANCE),
    column('column.report.financialStatements.classification', 'cashFlowClassification', 'reports.columns.cashFlowClassification', 'text', ACCOUNTING_TRIAL_BALANCE),
    column('column.report.financialStatements.amount', 'amount', 'reports.columns.amount', 'money', ACCOUNTING_TRIAL_BALANCE),
];
const RECONCILIATION_COLUMNS = [
    column('column.report.reconciliation.run', 'runId', 'reports.columns.runId', 'text', RECONCILIATION_RUN_BREAKS_LIST),
    column('column.report.reconciliation.break', 'breakId', 'reports.columns.breakId', 'text', RECONCILIATION_RUN_BREAKS_LIST),
    column('column.report.reconciliation.client', 'clientAccount', 'reports.columns.clientAccount', 'text', RECONCILIATION_RUN_BREAKS_LIST),
    column('column.report.reconciliation.currency', 'currency', 'reports.columns.currency', 'text', RECONCILIATION_RUN_BREAKS_LIST),
    column('column.report.reconciliation.severity', 'severity', 'reports.columns.severity', 'status', RECONCILIATION_RUN_BREAKS_LIST),
    column('column.report.reconciliation.status', 'status', 'reports.columns.status', 'status', RECONCILIATION_RUN_BREAKS_LIST),
    column('column.report.reconciliation.difference', 'difference', 'reports.columns.netDifference', 'money', RECONCILIATION_RUN_BREAKS_LIST),
    column('column.report.reconciliation.absoluteDifference', 'absoluteDifference', 'reports.columns.grossDifference', 'money', RECONCILIATION_RUN_BREAKS_LIST),
    column('column.report.reconciliation.sourceMismatch', 'sourceMismatch', 'reports.columns.sourceMismatch', 'text', RECONCILIATION_RUN_BREAKS_LIST),
    column('column.report.reconciliation.age', 'agingBusinessDays', 'reports.columns.agingBusinessDays', 'number', RECONCILIATION_RUN_BREAKS_LIST),
];
const TRANSACTION_ACTIVITY_COLUMNS = [
    column('column.report.transactionActivity.date', 'transactionDate', 'reports.columns.transactionDate', 'datetime', REPORTS_VIEW),
    column('column.report.transactionActivity.type', 'transactionType', 'reports.columns.transactionType', 'text', REPORTS_VIEW),
    column('column.report.transactionActivity.instrument', 'instrumentSymbol', 'reports.columns.instrument', 'text', REPORTS_VIEW),
    column('column.report.transactionActivity.portfolio', 'portfolioAccountId', 'reports.columns.portfolioAccount', 'text', REPORTS_VIEW),
    column('column.report.transactionActivity.quantity', 'quantity', 'reports.columns.quantity', 'number', REPORTS_VIEW),
    column('column.report.transactionActivity.amount', 'totalAmount', 'reports.columns.totalAmount', 'money', REPORTS_VIEW),
    column('column.report.transactionActivity.status', 'status', 'reports.columns.status', 'status', REPORTS_VIEW),
    column('column.report.transactionActivity.postingStatus', 'postingStatus', 'reports.columns.postingStatus', 'status', REPORTS_VIEW),
    column('column.report.transactionActivity.realizedGainLoss', 'realizedGainLoss', 'reports.columns.realizedGainLoss', 'money', REPORTS_VIEW),
];
function regulatoryReportDescriptor(type, id, titleKey, descriptionKey, generatorId) {
    return {
        ...metadata(id, MODULE_KEYS.REPORTS, FEATURE_KEYS.REPORTS_VIEW, REPORTS_VIEW, 'FINANCIAL', titleKey),
        reportType: type,
        titleKey: tk(titleKey),
        descriptionKey: tk(descriptionKey),
        dataSourceId: qdi(`query.reports.${type.toLowerCase()}`),
        columns: REPORT_COLUMNS,
        allowedFormats: ['csv', 'json'],
        formats: ['csv', 'json'],
        category: 'REGULATORY',
        scope: 'BRANCH',
        routeId: 'reports',
        parameterFormId: REPORT_PARAMETER_FORM.id,
        definitionFormId: REPORT_DEFINITION_FORM.id,
        runFormId: REPORT_RUN_FORM.id,
        generatorId,
        executionMode: 'GENERATED_ARTIFACT',
        operationBindings: {
            listOperationId: operation(REGULATORY_REPORT_RUN_LIST),
            createOperationId: operation(REGULATORY_REPORT_DEFINITION_CREATE),
            updateOperationId: operation(REGULATORY_REPORT_DEFINITION_UPDATE),
            disableOperationId: operation(REGULATORY_REPORT_DEFINITION_DISABLE),
            runOperationId: operation(REGULATORY_REPORT_RUN_GENERATE),
            validateOperationId: operation(REGULATORY_REPORT_RUN_VALIDATE),
            exportOperationId: operation(REGULATORY_REPORT_RUN_EXPORT),
            cancelOperationId: operation(REGULATORY_REPORT_RUN_CANCEL),
            retryOperationId: operation(REGULATORY_REPORT_RUN_RETRY),
        },
        lineage: {
            sourceSystem: 'regulatory-reporting',
            sourceEntities: ['ReportDefinition', 'ReportRun', 'ReportOutput'],
            calculationVersion: 'regulatory-v1',
        },
        implemented: true,
        auditPolicy: 'ALWAYS',
    };
}
function liveReportDescriptor(options) {
    return {
        ...metadata(options.id, MODULE_KEYS.REPORTS, FEATURE_KEYS.REPORTS_VIEW, options.operationId, 'FINANCIAL', options.titleKey),
        reportType: options.type,
        titleKey: tk(options.titleKey),
        descriptionKey: tk(options.descriptionKey),
        dataSourceId: qdi(options.dataSourceId),
        columns: options.columns,
        allowedFormats: ['json'],
        formats: ['json'],
        category: options.category,
        scope: options.scope,
        routeId: 'reports',
        parameterFormId: options.parameterFormId,
        runFormId: options.parameterFormId,
        ...(options.rowIdField ? { rowIdField: options.rowIdField } : {}),
        generatorId: options.generatorId,
        executionMode: 'LIVE_QUERY',
        operationBindings: {
            listOperationId: operation(options.operationId),
            runOperationId: operation(options.operationId),
            exportOperationId: operation(REPORT_EXPORT),
        },
        lineage: {
            sourceSystem: options.sourceSystem,
            sourceEntities: options.sourceEntities,
            calculationVersion: options.calculationVersion,
        },
        implemented: true,
        auditPolicy: 'ALWAYS',
    };
}
const CLIENT_STATEMENT_FIELDS = [
    field('field.reports.live.clientStatement.clientAccountId', 'clientAccountId', 'reports.parameters.clientAccount', 'select', undefined, {
        optionSource: {
            adapterId: 'api',
            operationId: OPERATION_IDS.ORGANIZATION_CLIENTS_LIST,
            valuePath: 'id',
            labelPath: 'fullName',
            allowSearch: true,
            minimumSearchLength: 0,
        },
    }),
    field('field.reports.live.clientStatement.portfolioAccountId', 'portfolioAccountId', 'reports.parameters.portfolioAccount', 'select', undefined, {
        optionSource: {
            adapterId: 'api',
            operationId: OPERATION_IDS.PORTFOLIO_ACCOUNT_LIST,
            valuePath: 'id',
            labelPath: 'accountNumber',
            allowSearch: true,
            minimumSearchLength: 0,
            queryDependencies: ['field.reports.live.clientStatement.clientAccountId'],
            queryParams: {
                clientAccountId: { fieldId: 'field.reports.live.clientStatement.clientAccountId' },
            },
        },
    }),
    field('field.reports.live.clientStatement.periodStart', 'periodStart', 'reports.parameters.periodStart', 'date'),
    field('field.reports.live.clientStatement.periodEnd', 'periodEnd', 'reports.parameters.periodEnd', 'date'),
    field('field.reports.live.clientStatement.cutoffAt', 'cutoffAt', 'reports.parameters.cutoffAt', 'date'),
];
const CLIENT_STATEMENT_REPORT_PARAMETER_FORM = reportParameterForm('form.report.clientStatement.parameters', CLIENT_GOVERNANCE_RECORD, CLIENT_STATEMENT_FIELDS);
export const REPORT_REGISTRY = Object.freeze({
    [REPORT_IDS.PORTFOLIO_HOLDINGS]: liveReportDescriptor({
        type: 'PORTFOLIO_HOLDINGS',
        id: REPORT_IDS.PORTFOLIO_HOLDINGS,
        titleKey: 'reports.types.portfolioHoldings',
        descriptionKey: 'reports.descriptions.portfolioHoldings',
        category: 'HOLDINGS',
        scope: 'BRANCH',
        operationId: HOLDINGS_VIEW,
        dataSourceId: 'query.reports.portfolioHoldings',
        generatorId: 'live.generator.portfolioHoldings',
        parameterFormId: String(HOLDINGS_REPORT_PARAMETER_FORM.id),
        columns: holdingsReportColumns(HOLDINGS_VIEW),
        sourceSystem: 'finance-facts',
        sourceEntities: ['PositionFact', 'ValuationFact', 'Instrument'],
        calculationVersion: 'holdings-snapshot-v1-latest-per-key',
    }),
    [REPORT_IDS.CLIENT_HOLDINGS]: liveReportDescriptor({
        type: 'CLIENT_HOLDINGS',
        id: REPORT_IDS.CLIENT_HOLDINGS,
        titleKey: 'reports.types.clientHoldings',
        descriptionKey: 'reports.descriptions.clientHoldings',
        category: 'HOLDINGS',
        scope: 'CLIENT',
        operationId: CLIENT_HOLDINGS_READ_OWN,
        dataSourceId: 'query.reports.clientHoldings',
        generatorId: 'live.generator.clientHoldings',
        parameterFormId: String(CLIENT_HOLDINGS_REPORT_PARAMETER_FORM.id),
        columns: holdingsReportColumns(CLIENT_HOLDINGS_READ_OWN),
        sourceSystem: 'finance-facts',
        sourceEntities: ['PositionFact', 'ValuationFact', 'Instrument'],
        calculationVersion: 'holdings-snapshot-v1-latest-per-key',
    }),
    [REPORT_IDS.ACCOUNTING_TRIAL_BALANCE]: liveReportDescriptor({
        type: 'ACCOUNTING_TRIAL_BALANCE',
        id: REPORT_IDS.ACCOUNTING_TRIAL_BALANCE,
        titleKey: 'reports.types.accountingTrialBalance',
        descriptionKey: 'reports.descriptions.accountingTrialBalance',
        category: 'ACCOUNTING',
        scope: 'ORGANIZATION',
        operationId: ACCOUNTING_TRIAL_BALANCE,
        dataSourceId: 'query.reports.accountingTrialBalance',
        generatorId: 'live.generator.accountingTrialBalance',
        parameterFormId: String(TRIAL_BALANCE_REPORT_PARAMETER_FORM.id),
        rowIdField: 'accountId',
        columns: TRIAL_BALANCE_COLUMNS,
        sourceSystem: 'accounting-ledger',
        sourceEntities: [
            'LedgerAccount',
            'JournalEntry',
            'JournalEntryLine',
            'FiscalYear',
            'FiscalPeriod',
        ],
        calculationVersion: 'trial-balance-v1-debit-positive',
    }),
    [REPORT_IDS.ACCOUNTING_FINANCIAL_STATEMENTS]: liveReportDescriptor({
        type: 'ACCOUNTING_FINANCIAL_STATEMENTS',
        id: REPORT_IDS.ACCOUNTING_FINANCIAL_STATEMENTS,
        titleKey: 'reports.types.accountingFinancialStatements',
        descriptionKey: 'reports.descriptions.accountingFinancialStatements',
        category: 'ACCOUNTING',
        scope: 'ORGANIZATION',
        operationId: ACCOUNTING_TRIAL_BALANCE,
        dataSourceId: 'query.reports.accountingFinancialStatements',
        generatorId: 'live.generator.accountingFinancialStatements',
        parameterFormId: String(FINANCIAL_STATEMENTS_REPORT_PARAMETER_FORM.id),
        rowIdField: 'id',
        columns: FINANCIAL_STATEMENTS_COLUMNS,
        sourceSystem: 'accounting-ledger',
        sourceEntities: ['LedgerAccount', 'AccountGroup', 'JournalEntry', 'JournalEntryLine', 'FiscalYear'],
        calculationVersion: 'financial-statements-v1-posted-ledger-direct-cash-flow',
    }),
    [REPORT_IDS.ACCOUNTING_PROFIT_AND_LOSS]: liveReportDescriptor({
        type: 'ACCOUNTING_PROFIT_AND_LOSS',
        id: REPORT_IDS.ACCOUNTING_PROFIT_AND_LOSS,
        titleKey: 'reports.types.accountingProfitAndLoss',
        descriptionKey: 'reports.descriptions.accountingProfitAndLoss',
        category: 'ACCOUNTING',
        scope: 'ORGANIZATION',
        operationId: ACCOUNTING_TRIAL_BALANCE,
        dataSourceId: 'query.reports.accountingProfitAndLoss',
        generatorId: 'live.generator.accountingFinancialStatements',
        parameterFormId: String(FINANCIAL_STATEMENTS_REPORT_PARAMETER_FORM.id),
        rowIdField: 'id',
        columns: FINANCIAL_STATEMENTS_COLUMNS,
        sourceSystem: 'accounting-ledger',
        sourceEntities: ['LedgerAccount', 'AccountGroup', 'JournalEntry', 'JournalEntryLine', 'FiscalYear'],
        calculationVersion: 'profit-and-loss-v1-revenue-expense-netted',
    }),
    [REPORT_IDS.RECONCILIATION_BREAKS]: liveReportDescriptor({
        type: 'RECONCILIATION_BREAKS',
        id: REPORT_IDS.RECONCILIATION_BREAKS,
        titleKey: 'reports.types.reconciliationBreaks',
        descriptionKey: 'reports.descriptions.reconciliationBreaks',
        category: 'RECONCILIATION',
        scope: 'BRANCH',
        operationId: RECONCILIATION_RUN_BREAKS_LIST,
        dataSourceId: 'query.reports.reconciliationBreaks',
        generatorId: 'live.generator.reconciliationBreaks',
        parameterFormId: String(RECONCILIATION_REPORT_PARAMETER_FORM.id),
        columns: RECONCILIATION_COLUMNS,
        sourceSystem: 'reconciliation-engine',
        sourceEntities: [
            'ReconciliationRun',
            'ReconciliationBreak',
            'ReconciliationAdjustment',
            'ExternalStatement',
        ],
        calculationVersion: 'reconciliation-exposure-v1',
    }),
    [REPORT_IDS.TRANSACTION_ACTIVITY]: liveReportDescriptor({
        type: 'TRANSACTION_ACTIVITY',
        id: REPORT_IDS.TRANSACTION_ACTIVITY,
        titleKey: 'reports.types.transactionActivity',
        descriptionKey: 'reports.descriptions.transactionActivity',
        category: 'TRANSACTIONS',
        scope: 'ORGANIZATION',
        operationId: REPORTS_VIEW,
        dataSourceId: 'query.reports.transactionActivity',
        generatorId: 'live.generator.transactionActivity',
        parameterFormId: String(TRANSACTION_ACTIVITY_REPORT_PARAMETER_FORM.id),
        rowIdField: 'id',
        columns: TRANSACTION_ACTIVITY_COLUMNS,
        sourceSystem: 'investment-book',
        sourceEntities: ['Transaction', 'RealizedGainLossFact', 'ValuationFact'],
        calculationVersion: 'transaction-activity-v1-latest-valuation-per-position',
    }),
    [REPORT_IDS.CLIENT_STATEMENT]: liveReportDescriptor({
        type: 'CLIENT_STATEMENT',
        id: REPORT_IDS.CLIENT_STATEMENT,
        titleKey: 'reports.types.clientStatement',
        descriptionKey: 'reports.descriptions.clientStatement',
        category: 'HOLDINGS',
        scope: 'CLIENT',
        operationId: CLIENT_GOVERNANCE_RECORD,
        dataSourceId: 'query.reports.clientStatement',
        generatorId: 'live.generator.clientStatement',
        parameterFormId: String(CLIENT_STATEMENT_REPORT_PARAMETER_FORM.id),
        rowIdField: 'positionFactId',
        columns: [
            column('column.report.clientStatement.positionFactId', 'positionFactId', 'reports.columns.id', 'text', CLIENT_GOVERNANCE_RECORD),
            column('column.report.clientStatement.portfolioAccountId', 'portfolioAccountId', 'reports.columns.portfolioAccount', 'text', CLIENT_GOVERNANCE_RECORD),
            column('column.report.clientStatement.instrumentSymbol', 'instrumentSymbol', 'reports.columns.symbol', 'text', CLIENT_GOVERNANCE_RECORD),
            column('column.report.clientStatement.currency', 'currency', 'reports.columns.currency', 'text', CLIENT_GOVERNANCE_RECORD),
            column('column.report.clientStatement.quantity', 'quantity', 'reports.columns.quantity', 'number', CLIENT_GOVERNANCE_RECORD),
            column('column.report.clientStatement.averageCostPerUnit', 'averageCostPerUnit', 'reports.columns.averageCostPerUnit', 'money', CLIENT_GOVERNANCE_RECORD),
            column('column.report.clientStatement.totalCost', 'totalCost', 'reports.columns.totalCost', 'money', CLIENT_GOVERNANCE_RECORD),
            column('column.report.clientStatement.marketPrice', 'marketPrice', 'reports.columns.marketPrice', 'money', CLIENT_GOVERNANCE_RECORD),
            column('column.report.clientStatement.marketValue', 'marketValue', 'reports.columns.marketValue', 'money', CLIENT_GOVERNANCE_RECORD),
            column('column.report.clientStatement.unrealizedGainLoss', 'unrealizedGainLoss', 'reports.columns.unrealizedGainLoss', 'money', CLIENT_GOVERNANCE_RECORD),
        ],
        sourceSystem: 'client-statement-engine',
        sourceEntities: ['PositionFact', 'ValuationFact', 'CashPosition', 'Transaction', 'ClientAccount'],
        calculationVersion: 'client-statement-v1',
    }),
    [REPORT_IDS.SEBON_MONTHLY]: regulatoryReportDescriptor('SEBON_MONTHLY', REPORT_IDS.SEBON_MONTHLY, 'reports.types.sebonMonthly', 'reports.descriptions.sebonMonthly', 'regulatory.generator.sebonMonthly'),
    [REPORT_IDS.IRD_CGT]: {
        ...regulatoryReportDescriptor('IRD_CGT', REPORT_IDS.IRD_CGT, 'reports.types.irdCgt', 'reports.descriptions.irdCgt', 'regulatory.generator.irdCgt'),
        runFormId: IRD_FISCAL_YEAR_RUN_FORM.id,
    },
    [REPORT_IDS.NRB_QUARTERLY]: regulatoryReportDescriptor('NRB_QUARTERLY', REPORT_IDS.NRB_QUARTERLY, 'reports.types.nrbQuarterly', 'reports.descriptions.nrbQuarterly', 'regulatory.generator.nrbQuarterly'),
    [REPORT_IDS.FIU_AML_CTF]: regulatoryReportDescriptor('FIU_AML_CTF', REPORT_IDS.FIU_AML_CTF, 'reports.types.fiuAmlCtf', 'reports.descriptions.fiuAmlCtf', 'regulatory.generator.fiuAmlCtf'),
});
function widget(id, titleKey, dataSourceId, position, operationId, widgetType, drilldownOperationId, settings, tableId) {
    return {
        ...metadata(id, MODULE_KEYS.DASHBOARD, FEATURE_KEYS.DASHBOARD_VIEW, operationId, 'FINANCIAL', titleKey),
        titleKey: tk(titleKey),
        dataSourceId: qdi(dataSourceId),
        position,
        widgetType,
        drilldownOperationId: drilldownOperationId ? operation(drilldownOperationId) : undefined,
        settings,
        tableId,
    };
}
export const DASHBOARD_REGISTRY = Object.freeze({
    [DASHBOARD_IDS.PLATFORM]: {
        ...metadata(DASHBOARD_IDS.PLATFORM, MODULE_KEYS.PLATFORM, FEATURE_KEYS.DASHBOARD_VIEW, PLATFORM_DASHBOARD_READ, 'INTERNAL', 'a11y.dashboard.platform'),
        titleKey: tk('dashboard.platform.title'),
        scope: 'PLATFORM',
        refreshIntervalSeconds: 30,
        emptyStateKey: tk('dashboard.platform.empty'),
        emptyStateDescriptionKey: tk('dashboard.platform.emptyDescription'),
        partialStateKey: tk('dashboard.platform.partial'),
        widgets: [
            widget('widget.platform.branchHealth', 'dashboard.platform.branchHealth', 'query.dashboard.platform.branchHealth', { x: 0, y: 0, w: 4, h: 2 }, PLATFORM_DASHBOARD_READ, 'metric'),
            widget('widget.platform.jobFailures', 'dashboard.platform.jobFailures', 'query.dashboard.platform.jobFailures', { x: 4, y: 0, w: 4, h: 2 }, PLATFORM_DASHBOARD_READ, 'metric'),
            widget('widget.platform.authorizationPosture', 'dashboard.platform.authorizationPosture', 'query.dashboard.platform.authorizationPosture', { x: 8, y: 0, w: 4, h: 2 }, PLATFORM_DASHBOARD_READ, 'dataQuality'),
        ],
    },
    [DASHBOARD_IDS.ORGANIZATION]: {
        ...metadata(DASHBOARD_IDS.ORGANIZATION, MODULE_KEYS.DASHBOARD, FEATURE_KEYS.DASHBOARD_VIEW, DASHBOARD_VIEW, 'FINANCIAL', 'a11y.dashboard.organization'),
        titleKey: tk('dashboard.organization.title'),
        scope: 'BRANCH',
        refreshIntervalSeconds: 60,
        emptyStateKey: tk('dashboard.organization.empty'),
        emptyStateDescriptionKey: tk('dashboard.organization.emptyDescription'),
        partialStateKey: tk('dashboard.organization.partial'),
        widgets: [
            widget('widget.organization.marketValue', 'dashboard.widgets.marketValue', 'query.dashboard.portfolioSummary', { x: 0, y: 0, w: 4, h: 2 }, DASHBOARD_VIEW, 'metric', undefined, { valueFormat: 'currency' }),
            widget('widget.organization.costBasis', 'dashboard.widgets.costBasis', 'query.dashboard.portfolioSummary', { x: 4, y: 0, w: 4, h: 2 }, DASHBOARD_VIEW, 'metric', undefined, { valueFormat: 'currency' }),
            widget('widget.organization.unrealizedGain', 'dashboard.widgets.unrealizedGain', 'query.dashboard.portfolioSummary', { x: 8, y: 0, w: 4, h: 2 }, DASHBOARD_VIEW, 'metric', undefined, { valueFormat: 'currency' }),
            widget('widget.organization.pendingApprovals', 'dashboard.widgets.pendingApprovals', 'query.dashboard.pendingApprovals', { x: 0, y: 2, w: 4, h: 2 }, DASHBOARD_VIEW, 'task', TRANSACTION_LIST),
            widget('widget.organization.failedImports', 'dashboard.widgets.failedImports', 'query.dashboard.failedImports', { x: 4, y: 2, w: 4, h: 2 }, DASHBOARD_VIEW, 'alert', IMPORT_LIST),
            widget('widget.organization.reconciliationBreaks', 'dashboard.widgets.reconciliationBreaks', 'query.dashboard.reconciliationBreaks', { x: 8, y: 2, w: 4, h: 2 }, DASHBOARD_VIEW, 'alert', RECONCILIATION_LIST),
            widget('widget.organization.holdings', 'dashboard.widgets.holdings', 'query.dashboard.holdings', { x: 0, y: 4, w: 12, h: 5 }, DASHBOARD_VIEW, 'table', HOLDINGS_VIEW, undefined, 'table.dashboard.holdings'),
        ],
    },
    [DASHBOARD_IDS.CLIENT]: {
        ...metadata(DASHBOARD_IDS.CLIENT, MODULE_KEYS.DASHBOARD, FEATURE_KEYS.DASHBOARD_VIEW, CLIENT_DASHBOARD_READ, 'FINANCIAL', 'a11y.dashboard.client'),
        titleKey: tk('dashboard.client.title'),
        scope: 'CLIENT',
        refreshIntervalSeconds: 120,
        emptyStateKey: tk('dashboard.client.empty'),
        emptyStateDescriptionKey: tk('dashboard.client.emptyDescription'),
        partialStateKey: tk('dashboard.client.partial'),
        widgets: [
            widget('widget.client.marketValue', 'dashboard.widgets.marketValue', 'query.dashboard.portfolioSummary', { x: 0, y: 0, w: 4, h: 2 }, CLIENT_DASHBOARD_READ, 'metric', undefined, { valueFormat: 'currency' }),
            widget('widget.client.costBasis', 'dashboard.widgets.costBasis', 'query.dashboard.portfolioSummary', { x: 4, y: 0, w: 4, h: 2 }, CLIENT_DASHBOARD_READ, 'metric', undefined, { valueFormat: 'currency' }),
            widget('widget.client.unrealizedGain', 'dashboard.widgets.unrealizedGain', 'query.dashboard.portfolioSummary', { x: 8, y: 0, w: 4, h: 2 }, CLIENT_DASHBOARD_READ, 'metric', undefined, { valueFormat: 'currency' }),
            widget('widget.client.holdings', 'dashboard.widgets.holdings', 'query.dashboard.holdings', { x: 0, y: 2, w: 12, h: 5 }, CLIENT_DASHBOARD_READ, 'table', CLIENT_HOLDINGS_READ_OWN, undefined, 'table.dashboard.holdings'),
        ],
    },
});
export const REPORT_DASHBOARD_FORMS = Object.freeze({
    [REPORT_PARAMETER_FORM.id]: REPORT_PARAMETER_FORM,
    [HOLDINGS_REPORT_PARAMETER_FORM.id]: HOLDINGS_REPORT_PARAMETER_FORM,
    [CLIENT_HOLDINGS_REPORT_PARAMETER_FORM.id]: CLIENT_HOLDINGS_REPORT_PARAMETER_FORM,
    [TRIAL_BALANCE_REPORT_PARAMETER_FORM.id]: TRIAL_BALANCE_REPORT_PARAMETER_FORM,
    [FINANCIAL_STATEMENTS_REPORT_PARAMETER_FORM.id]: FINANCIAL_STATEMENTS_REPORT_PARAMETER_FORM,
    [RECONCILIATION_REPORT_PARAMETER_FORM.id]: RECONCILIATION_REPORT_PARAMETER_FORM,
    [TRANSACTION_ACTIVITY_REPORT_PARAMETER_FORM.id]: TRANSACTION_ACTIVITY_REPORT_PARAMETER_FORM,
    [CLIENT_STATEMENT_REPORT_PARAMETER_FORM.id]: CLIENT_STATEMENT_REPORT_PARAMETER_FORM,
    [REPORT_DEFINITION_FORM.id]: REPORT_DEFINITION_FORM,
    [REPORT_RUN_FORM.id]: REPORT_RUN_FORM,
    [IRD_FISCAL_YEAR_RUN_FORM.id]: IRD_FISCAL_YEAR_RUN_FORM,
});
